import { useQuery } from "@tanstack/react-query";
import { House, LayoutGrid, MessageCircle, PackageSearch, Search, ShoppingBag } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../state/cart";
import { Wordmark } from "./brand";
import { HelpWidget } from "./HelpWidget";

type Content = { settings: Record<string, any> };
export type StorefrontContext = { openHelp: () => void };

/** Pages where the bottom tab bar would compete with a page-level action bar or a focused checkout. */
const focusedRoute = (path: string) => path.startsWith("/products/") || path.startsWith("/checkout") || path.startsWith("/payment/");

export function PublicLayout() {
  const { count } = useCart();
  const location = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);
  const content = useQuery({ queryKey: ["content"], queryFn: () => api<Content>("/content") });
  const settings = content.data?.settings;
  const showTabs = !focusedRoute(location.pathname);
  const bump = useCountBump(count);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior }); }, [location.pathname]);

  const desktopLink = ({ isActive }: { isActive: boolean }) =>
    `relative py-2 text-[15px] font-bold transition-colors after:absolute after:inset-x-0 after:-bottom-0.5 after:h-[1.5px] after:origin-left after:bg-ribbon after:transition-transform after:duration-300 ${isActive ? "text-cocoa after:scale-x-100" : "text-cocoa/65 after:scale-x-0 hover:text-cocoa hover:after:scale-x-100"}`;

  return (
    <div className="min-h-screen bg-white">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-full focus:bg-cocoa focus:px-4 focus:py-2 focus:text-white">Skip to content</a>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm">
        <div className="container-page flex h-16 items-center gap-8">
          <Wordmark />
          <nav className="hidden items-center gap-7 md:flex" aria-label="Main">
            <NavLink to="/shop" className={desktopLink}>Shop</NavLink>
            <NavLink to="/track" className={desktopLink}>Track an order</NavLink>
            <button type="button" onClick={() => setHelpOpen(true)} className={desktopLink({ isActive: false })}>Ask Cass</button>
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <Link to="/shop" aria-label="Search the shop" className="hidden size-11 place-items-center rounded-full text-cocoa transition hover:bg-frosting md:grid"><Search size={20} /></Link>
            <button type="button" onClick={() => setHelpOpen(true)} aria-label="Ask Cass a question" className="grid size-11 place-items-center rounded-full text-cocoa transition hover:bg-frosting md:hidden"><MessageCircle size={21} /></button>
            <Link
              to="/cart"
              aria-label={`Bag, ${count} item${count === 1 ? "" : "s"}`}
              className={`relative items-center gap-2 rounded-full px-2.5 py-2 text-[15px] font-bold text-cocoa transition hover:bg-frosting md:flex md:px-3.5 ${showTabs ? "hidden" : "flex"}`}
            >
              <ShoppingBag size={21} />
              <span className="hidden md:inline">Bag</span>
              <CountBadge count={count} bump={bump} className="absolute -right-0.5 top-0.5 md:static" />
            </Link>
          </div>
        </div>
        <div className="ruler" aria-hidden="true" />
      </header>

      <main id="main"><Outlet context={{ openHelp: () => setHelpOpen(true) } satisfies StorefrontContext} /></main>

      <SiteFooter settings={settings} onHelp={() => setHelpOpen(true)} padForTabs={showTabs || location.pathname.startsWith("/products/")} />

      {showTabs && <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-sm md:hidden">
        <div className="grid h-16 grid-cols-4">
          <TabLink to="/" end icon={House} label="Home" />
          <TabLink to="/shop" icon={LayoutGrid} label="Shop" />
          <TabLink to="/track" icon={PackageSearch} label="Track" />
          <TabLink to="/cart" icon={ShoppingBag} label="Bag" badge={<CountBadge count={count} bump={bump} className="absolute -right-2.5 -top-1.5" />} />
        </div>
      </nav>}

      <HelpWidget open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}

function TabLink({ to, icon: Icon, label, end, badge }: { to: string; icon: typeof House; label: string; end?: boolean; badge?: React.ReactNode }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 text-[11px] font-bold transition-colors ${isActive ? "text-cocoa" : "text-muted"}`}>
      {({ isActive }) => <>
        <span className={`absolute top-0 h-[3px] w-6 rounded-b-full bg-ribbon transition-transform duration-300 ${isActive ? "scale-x-100" : "scale-x-0"}`} />
        <span className="relative"><Icon size={21} strokeWidth={isActive ? 2.2 : 1.8} />{badge}</span>
        {label}
      </>}
    </NavLink>
  );
}

function CountBadge({ count, bump, className = "" }: { count: number; bump: number; className?: string }) {
  if (count <= 0) return null;
  return <span key={bump} className={`grid h-[18px] min-w-[18px] place-items-center rounded-full bg-ribbon px-1 text-[11px] font-bold leading-none text-white ${bump ? "bump" : ""} ${className}`}>{count}</span>;
}

/** Returns a counter that changes whenever the bag count goes up, used to replay the badge "bump". */
function useCountBump(count: number) {
  const previous = useRef(count);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    if (count > previous.current) setBump((value) => value + 1);
    previous.current = count;
  }, [count]);
  return bump;
}

function SiteFooter({ settings, onHelp, padForTabs }: { settings?: Record<string, any>; onHelp: () => void; padForTabs: boolean }) {
  const contact = settings?.contact ?? {};
  const social: Record<string, string> = settings?.social ?? {};
  const socialLinks = Object.entries(social).filter(([, url]) => typeof url === "string" && url.length > 0);
  return (
    <footer className="mt-24 bg-frosting">
      <div className="ruler" aria-hidden="true" />
      <div className="container-page grid grid-cols-2 gap-x-6 gap-y-10 py-14 md:grid-cols-[1.4fr_1fr_1fr_1.2fr] md:gap-8">
        <div className="col-span-2 md:col-span-1">
          <Wordmark />
          <p className="mt-4 max-w-[26ch] font-display text-2xl leading-tight text-cocoa">{settings?.brand?.tagline ?? "Tiny things, made with love."}</p>
          {contact.pickupAddress && <p className="mt-3 text-sm text-muted">Made by hand in {contact.pickupAddress}</p>}
        </div>
        <FooterColumn title="Shop">
          <Link to="/shop" className="hover:text-ribbon-ink">Everything</Link>
          <Link to="/shop?sort=newest" className="hover:text-ribbon-ink">Newest pieces</Link>
          <Link to="/cart" className="hover:text-ribbon-ink">Your bag</Link>
        </FooterColumn>
        <FooterColumn title="Help">
          <Link to="/track" className="hover:text-ribbon-ink">Track an order</Link>
          <button type="button" onClick={onHelp} className="text-left hover:text-ribbon-ink">Ask Cass</button>
          <Link to="/privacy-policy" className="hover:text-ribbon-ink">Privacy policy</Link>
          <Link to="/terms-of-use" className="hover:text-ribbon-ink">Terms of use</Link>
        </FooterColumn>
        <FooterColumn title="Say hello" className="col-span-2 md:col-span-1">
          {contact.email && <a href={`mailto:${contact.email}`} className="break-all hover:text-ribbon-ink">{contact.email}</a>}
          {contact.mobile && <a href={`tel:${String(contact.mobile).replace(/\s/g, "")}`} className="hover:text-ribbon-ink">{contact.mobile}</a>}
          {socialLinks.length > 0 && <span className="flex flex-wrap gap-x-4 gap-y-1">
            {socialLinks.map(([network, url]) => <a key={network} href={url} target="_blank" rel="noreferrer" className="capitalize text-cocoa underline decoration-cocoa/25 underline-offset-4 hover:text-ribbon-ink">{network === "tiktok" ? "TikTok" : network}</a>)}
          </span>}
        </FooterColumn>
      </div>
      <div className={`container-page ${padForTabs ? "pb-20 md:pb-0" : ""}`}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line py-6 text-[13px] text-muted">
          <span>© {new Date().getFullYear()} Minis by Cass</span>
          <Link to="/admin/login" className="hover:text-cocoa">Studio login</Link>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children, className = "" }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <p className="font-hand text-xl leading-none text-ribbon-ink">{title}</p>
      <div className="mt-4 grid gap-2.5 text-[15px] text-cocoa">{children}</div>
    </div>
  );
}
